import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import {
  Trophy,
  Star,
  PlusCircle,
  FlaskConical,
  Award,
  Target,
  Medal,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@/common/components/ui/chart";
import { format } from "date-fns";

const chartConfig = {
  visitors: {
    label: "Rating Progress",
  },
  desktop: {
    label: "Rating",
    color: "hsl(var(--chart-1))",
  },
};

const StatsCard = ({ title, value, icon }) => (
  <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all">
    <div className="flex flex-col gap-1">
      <div className="flex justify-between items-start">
        <span className="text-gray-600 text-sm">{title}</span>
        {icon}
      </div>
      <span className="text-2xl font-semibold mt-2">{value}</span>
    </div>
  </div>
);

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Codeforces = () => {
  const navigate = useNavigate();
  const {
    user: { profile: currentUser },
  } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${
          import.meta.env.VITE_CLIENT_FUNCTION_BASE_URL
        }/client_dashboard/codeforces/${currentUser.id}`
      );
      const result = await response.json();

      if (result.status === "success") {
        setData(result.data);
      } else {
        console.error("Error in fetching data", result);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const refreshUser = async () => {
    try {
      setRefreshing(true);
      const res = await fetch(
        `${
          import.meta.env.VITE_CLIENT_FUNCTION_BASE_URL
        }/codeforces/refresh_user/${currentUser.id}`,
        {
          method: "POST",
        }
      );

      if (!res.ok) {
        throw new Error("Error in refreshing user");
      }

      fetchData();
    } catch (error) {
      console.error("Error in refreshing user", error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  if (loading) {
    return (
      <div className="flex flex-col gap-6 w-full max-w-[1200px] mx-auto p-4 animate-pulse">
        {/* Verification Banner Skeleton */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-700/20 to-slate-900/20 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-gray-200"></div>
            <div>
              <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-64 bg-gray-200 rounded"></div>
            </div>
          </div>
          <div className="h-10 w-24 bg-gray-200 rounded"></div>
        </div>

        {/* Refresh Button Skeleton */}
        <div className="flex flex-col gap-1 items-end w-full">
          <div className="h-10 w-32 bg-gray-200 rounded"></div>
          <div className="h-4 w-48 bg-gray-200 rounded"></div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-start">
                  <div className="h-4 w-32 bg-gray-200 rounded"></div>
                  <div className="h-5 w-5 bg-gray-200 rounded"></div>
                </div>
                <div className="h-8 w-24 bg-gray-200 rounded mt-2"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Profile Details Card Skeleton */}
        <div className="grid grid-cols-1 md:grid-rows-1 gap-6">
          <Card className="flex-1 p-6 bg-white shadow-sm">
            <div className="mb-6">
              <div className="h-6 w-36 bg-gray-200 rounded"></div>
            </div>
            <div className="space-y-6">
              {/* Metrics */}
              <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg"
                  >
                    <div className="h-8 w-8 rounded bg-gray-200"></div>
                    <div>
                      <div className="h-4 w-24 bg-gray-200 rounded mb-2"></div>
                      <div className="h-6 w-16 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* User Info */}
              <div className="grid grid-cols-2 gap-8 pt-4">
                {[1, 2].map((i) => (
                  <div key={i}>
                    <div className="h-4 w-20 bg-gray-200 rounded mb-2"></div>
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Rating Graph Card Skeleton */}
          <Card className="flex-1 p-6 bg-white shadow-sm">
            <div className="mb-6">
              <div className="h-6 w-36 bg-gray-200 rounded"></div>
            </div>
            <div className="h-[300px] bg-gray-100 rounded-lg"></div>
          </Card>
        </div>

        {/* Contest History Table Skeleton */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="h-6 w-36 bg-gray-200 rounded mb-6"></div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  {[
                    "Contest",
                    "Problems Solved",
                    "Rank",
                    "Rating Change",
                    "New Rating",
                  ].map((header) => (
                    <th key={header} className="text-left py-3 px-4">
                      <div className="h-4 w-24 bg-gray-200 rounded mx-auto"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4].map((i) => (
                  <tr key={i} className="border-b last:border-b-0">
                    <td className="py-3 px-4">
                      <div className="space-y-2">
                        <div className="h-5 w-48 bg-gray-200 rounded"></div>
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="h-5 w-16 bg-gray-200 rounded mx-auto"></div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="h-5 w-16 bg-gray-200 rounded mx-auto"></div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="h-5 w-16 bg-gray-200 rounded mx-auto"></div>
                    </td>
                    <td className="text-center py-3 px-4">
                      <div className="h-5 w-16 bg-gray-200 rounded mx-auto"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="w-full h-full flex items-center justify-center flex-col gap-3">
        <PlusCircle className="h-32 w-32 text-blue-500" />
        <div className="flex flex-col gap-3 items-center justify-center">
          <h1 className="font-semibold text-3xl">Profile is not connected</h1>
          <p>
            Showcase Your Competitive Programming Skills by connecting your
            profile and verify it.
          </p>
          <Button
            onClick={() => navigate("/home/edit/profile/otherprofiles")}
            className="bg-blue-500 text-white"
          >
            Connect
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-[1200px] mx-auto p-4">
      {!data?.verified && (
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-700 to-slate-900 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <Trophy className="h-12 w-12 text-white opacity-80" />
            <div>
              <h2 className="text-xl font-semibold text-white">
                Profile is not verified
              </h2>
              <p className="text-slate-300">
                Verify your profile to showcase your competitive programming
                skills
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/home/edit/profile/otherprofiles")}
            className="bg-white text-black hover:bg-gray-100"
          >
            Verify
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-1 items-items justify-end w-full">
        <Button
          disabled={refreshing}
          onClick={refreshUser}
          className="self-end"
        >
          {refreshing ? "Refreshing..." : "Refresh Data"}
        </Button>
        <span className="self-end text-sm text-gray-500">
          Last Refresh : {format(new Date(data?.lastUpdated), "dd MMMM yyyy")}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatsCard
          title="Total Problems Solved"
          value={data?.totalSolved ?? 0}
          icon={<FlaskConical className="h-5 w-5 text-blue-500" />}
        />
        <StatsCard
          title="Contests Attended"
          value={data?.contestsAttended ?? 0}
          icon={<Award className="h-5 w-5 text-green-500" />}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-rows-1 gap-6">
        {/* Profile Details Card */}
        <Card className="flex-1 p-6 bg-white shadow-sm">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">Profile Details</h2>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-3 bg-orange-50 p-4 rounded-lg">
                <Trophy className="h-8 w-8 text-orange-500" />
                <div>
                  <p className="text-sm text-gray-600">Current Rating</p>
                  <p className="text-xl font-bold">{data?.currentRating}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-purple-50 p-4 rounded-lg">
                <Target className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="text-sm text-gray-600">Max Rating</p>
                  <p className="text-xl font-bold">{data?.maxRating}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg">
                <Medal className="h-8 w-8 text-yellow-500" />
                <div>
                  <p className="text-sm text-gray-600">Max Rank</p>
                  <p className="text-xl font-bold capitalize">
                    {data?.maxRank}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 pt-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Username</p>
                <p className="font-semibold">{data?.username}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Full Name</p>
                <p className="font-semibold">{data?.fullName}</p>
              </div>
            </div>
          </div>
        </Card>
        {/* Rating Graph Card */}
        <Card className="flex-1 p-6 bg-white shadow-sm">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">Rating Progress</h2>
          </div>
          <div className="h-[300px]">
            <ChartContainer config={chartConfig} className="w-full h-full">
              <AreaChart data={data?.contests_participated || []}>
                <defs>
                  <linearGradient id="fillRating" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff9800" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#ff9800" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="created_at"
                  tickFormatter={formatDate}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                />
                <ChartTooltip
                  cursor={false}
                  content={<CustomTooltip indicator="dot" />}
                />
                <Area
                  type="monotone"
                  dataKey="rating"
                  stroke="#ff9800"
                  fill="url(#fillRating)"
                  strokeWidth={2}
                />
                {/* <ChartLegend content={<ChartLegendContent />} /> */}
              </AreaChart>
            </ChartContainer>
          </div>
        </Card>
      </div>

      {/* Contest History Table */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-6">Contest History</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b">
              <tr className="text-sm text-gray-600">
                <th className="text-left py-3 px-4">Contest</th>
                <th className="text-center py-3 px-4">Problems Solved</th>
                <th className="text-center py-3 px-4">Rank</th>
                <th className="text-center py-3 px-4">Rating Change</th>
                <th className="text-center py-3 px-4">New Rating</th>
              </tr>
            </thead>
            <tbody>
              {data?.contests_participated?.map((contest) => (
                <tr
                  key={contest.id}
                  className="border-b last:border-b-0 hover:bg-gray-50"
                >
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium">
                        {contest.codeforces_contest.title}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(contest.codeforces_contest.start_time)}
                      </p>
                    </div>
                  </td>
                  <td className="text-center py-3 px-4">
                    {contest.problems_solved}/
                    {contest.codeforces_contest.total_problems}
                  </td>
                  <td className="text-center py-3 px-4">{contest.rank}</td>
                  <td className="text-center py-3 px-4">
                    <span
                      className={
                        contest.rating_change >= 0
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {contest.rating_change >= 0 ? "+" : ""}
                      {contest.rating_change}
                    </span>
                  </td>
                  <td className="text-center py-3 px-4">{contest.rating}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Codeforces;

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload;

    return (
      <div className="rounded-lg border bg-white p-2 shadow-sm text-sm text-gray-700">
        <div className="font-semibold text-black">
          {data.codeforces_contest.title}
        </div>
        <div>Rating: {data.rating}</div>
        <div className="text-xs text-gray-500">
          {formatDate(data.created_at)}
        </div>
      </div>
    );
  }

  return null;
};
